import {
  decideFrenchRegion,
  regionAddressKey,
  type AddressResolutionSuggestion,
  type RegionAddress,
} from '@erp/shared';
import { useEffect, useRef } from 'react';
import { Loader2, MapPin, TriangleAlert } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { InlineHelp } from '@/features/help';
import { useAddressResolution } from '../api/use-address-resolution';

export interface AddressResolutionAssistProps {
  countryCode: string | null | undefined;
  postalCode: string | null | undefined;
  city?: string | null | undefined;
  region?: string | null | undefined;
  regionOrigin?: 'AUTO' | 'MANUAL' | null | undefined;
  /** Form resets hydrate stored provenance; only user edits invalidate that assignment. */
  addressDirty?: boolean;
  onResolve?: (change: {
    region: string | null;
    regionOrigin: 'AUTO' | null;
    city?: string;
  }) => void;
  disabled?: boolean;
  onSelect: (suggestion: AddressResolutionSuggestion) => void;
}

function suggestionLabel(suggestion: AddressResolutionSuggestion): string {
  return [
    suggestion.city,
    [suggestion.districtCode, suggestion.district].filter(Boolean).join(' – ') || null,
    suggestion.region,
  ]
    .filter((value, index, all) => value !== null && all.indexOf(value) === index)
    .join(' · ');
}

export function AddressResolutionAssist({
  countryCode,
  postalCode,
  city,
  region,
  regionOrigin,
  addressDirty = true,
  onResolve,
  disabled = false,
  onSelect,
}: AddressResolutionAssistProps): JSX.Element | null {
  const { t } = useTranslation();
  countryCode = countryCode?.trim().toUpperCase();
  const query = useAddressResolution(countryCode, postalCode);
  const address: RegionAddress = { countryCode, postalCode, city, region, regionOrigin };
  const key = regionAddressKey(address);
  const lastKey = useRef(key);
  const decision = decideFrenchRegion(address, query.data?.data ?? []);
  const available =
    !query.isError && !query.isFetching && query.data?.source.cacheStatus !== 'STALE';
  useEffect(() => {
    const changed = lastKey.current !== key;
    lastKey.current = key;
    if (disabled || onResolve === undefined) return;
    if (changed && addressDirty && regionOrigin === 'AUTO') {
      onResolve({ region: null, regionOrigin: null });
      return;
    }
    if (!available || decision.status !== 'RESOLVED' || (region?.trim() && regionOrigin !== 'AUTO'))
      return;
    const nextCity = !city?.trim() && decision.city !== null ? decision.city : undefined;
    if (region === decision.region && regionOrigin === 'AUTO' && nextCity === undefined) return;
    lastKey.current = regionAddressKey({ countryCode, postalCode, city: nextCity ?? city });
    onResolve({
      region: decision.region,
      regionOrigin: 'AUTO',
      ...(nextCity === undefined ? {} : { city: nextCity }),
    });
  }, [
    addressDirty,
    available,
    city,
    countryCode,
    decision.city,
    decision.region,
    decision.status,
    disabled,
    key,
    onResolve,
    postalCode,
    region,
    regionOrigin,
  ]);
  const supported = countryCode === 'DE' || countryCode === 'FR';
  const complete = /^\d{5}$/.test(postalCode?.trim() ?? '');
  if (!supported || !complete) return null;

  if (query.isPending || query.isFetching) {
    return (
      <p className="flex min-h-11 items-center gap-2 text-sm text-muted-foreground" role="status">
        <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
        {t('addressResolution.loading')}
      </p>
    );
  }
  if (query.isError) {
    return (
      <p
        className="flex items-start gap-2 text-sm text-amber-700 dark:text-amber-400"
        role="status"
      >
        <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
        {t('addressResolution.errorFallback')}
      </p>
    );
  }
  if (query.data === undefined || query.data.data.length === 0) {
    return <p className="text-sm text-muted-foreground">{t('addressResolution.noResults')}</p>;
  }

  const automatic = countryCode === 'FR' && onResolve !== undefined;
  const regionHint = automatic ? (
    <div role="status" className="text-sm text-muted-foreground">
      {query.data?.source.cacheStatus === 'STALE' ? (
        t('addressResolution.status.UNAVAILABLE')
      ) : regionOrigin === 'AUTO' &&
        region === decision.region &&
        decision.status === 'RESOLVED' ? (
        t('addressResolution.automatic')
      ) : decision.status === 'RESOLVED' && region?.trim() && region !== decision.region ? (
        <Button
          type="button"
          variant="outline"
          disabled={disabled}
          onClick={() => onResolve({ region: decision.region, regionOrigin: 'AUTO' })}
        >
          {t('addressResolution.adoptRegion', { region: decision.region })}
        </Button>
      ) : decision.status !== 'RESOLVED' ? (
        t(`addressResolution.status.${decision.status}`)
      ) : null}
    </div>
  ) : null;
  const suggestions = query.data.data.filter(
    (entry) => entry.countryCode === countryCode && entry.postalCode === postalCode?.trim(),
  );
  if (suggestions.length === 0) return null;

  const source = (
    <div className="flex items-center gap-1">
      <a
        href={query.data.source.sourceUrl}
        target="_blank"
        rel="noreferrer"
        className="text-xs text-muted-foreground underline-offset-2 hover:underline"
      >
        {t('addressResolution.source', { source: query.data.source.name })}
      </a>
      <InlineHelp
        articleId="partners.customer-file"
        anchor="postcode-resolution"
        label={t('addressResolution.help')}
      />
    </div>
  );

  if (suggestions.length === 1) {
    const suggestion = suggestions[0]!;
    return (
      <div className="space-y-1">
        {regionHint}
        <Button
          type="button"
          variant="outline"
          className="min-h-11 w-full justify-start whitespace-normal text-left font-normal"
          disabled={disabled}
          onClick={() => onSelect(suggestion)}
        >
          <MapPin className="mr-2 h-4 w-4 shrink-0" aria-hidden="true" />
          {t('addressResolution.useSuggestion', { value: suggestionLabel(suggestion) })}
        </Button>
        {source}
      </div>
    );
  }

  return (
    <div className="space-y-1">
      {regionHint}
      <Select
        key={`${countryCode}:${postalCode}`}
        value=""
        disabled={disabled}
        onValueChange={(id) => {
          const suggestion = suggestions.find((entry) => entry.id === id);
          if (suggestion !== undefined) onSelect(suggestion);
        }}
      >
        <SelectTrigger aria-label={t('addressResolution.choose')} className="min-h-11">
          <SelectValue
            placeholder={t('addressResolution.chooseCount', { count: suggestions.length })}
          />
        </SelectTrigger>
        <SelectContent>
          {suggestions.map((suggestion) => (
            <SelectItem key={suggestion.id} value={suggestion.id}>
              {suggestionLabel(suggestion)}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {source}
    </div>
  );
}
